import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, Subject, takeUntil } from 'rxjs';
import { IExam } from '@gray/domain/models/exam/exam.model';
import { ILesson } from '@gray/domain/models/lesson/lesson.model';
import { ILessonTopic } from '@gray/domain/models/lesson/lesson-topic.model';
import { CoachCatalogService } from '../../../../core/services/coach-catalog.service';
import { CoachStudentPlanService } from '../../../../core/services/coach-student-plan.service';

/**
 * Adds one item to a week of the student's plan: either a lesson topic or a free note, the two
 * kinds the student's own planner can create.
 */
@Component({
  selector: 'coach-planner-item-modal',
  templateUrl: './planner-item-modal.component.html',
  styleUrl: './planner-item-modal.component.scss',
})
export class PlannerItemModalComponent implements OnInit, OnDestroy {
  @Input() studentId!: number;
  @Input() periodId!: number;
  @Input() weekId!: number;
  /** Preselected when the coach used the button on a lesson column. */
  @Input() lesson?: ILesson | null;

  exams: IExam[] = [];
  lessons: ILesson[] = [];
  topics: ILessonTopic[] = [];

  /** A free note needs no lesson at all, so the two modes have different required fields. */
  mode: 'topic' | 'text' = 'topic';

  fg: FormGroup = this.formBuilder.group({
    examId: [null, [Validators.required]],
    lessonId: [null, [Validators.required]],
    lessonTopicId: [null, [Validators.required]],
    message: [null],
  });

  isLoading = false;
  isSubmitted = false;
  topicsIsLoading = false;

  destroyed$ = new Subject();

  constructor(
    public modal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private catalogService: CoachCatalogService,
    private planService: CoachStudentPlanService,
  ) {
  }

  ngOnInit(): void {
    this.getExams();
  }

  getExams() {
    this.catalogService.getExams()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(r => {
        this.exams = (r.data?.result ?? r.data ?? []) as IExam[];
      });
  }

  onExamChanged(examId?: number) {
    this.fg.patchValue({lessonId: null, lessonTopicId: null});
    this.lessons = [];
    this.topics = [];
    if (!examId) {
      return;
    }

    this.catalogService.getExamLessons(examId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(r => {
        this.lessons = (r.data?.result ?? r.data ?? []) as ILesson[];
        // The column the coach started from picks itself when the exam carries it.
        const preselected = this.lessons.find(lesson => lesson.id === this.lesson?.id);
        if (preselected) {
          this.fg.patchValue({lessonId: preselected.id});
          this.onLessonChanged(preselected.id);
        }
      });
  }

  onLessonChanged(lessonId?: number) {
    this.fg.patchValue({lessonTopicId: null});
    this.topics = [];

    const examId = this.fg.value.examId;
    if (!examId || !lessonId) {
      return;
    }

    this.topicsIsLoading = true;
    this.catalogService.getExamLessonTopics(examId, lessonId)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.topicsIsLoading = false))
      .subscribe(r => {
        this.topics = (r.data?.result ?? r.data ?? []) as ILessonTopic[];
      });
  }

  setMode(mode: 'topic' | 'text') {
    this.mode = mode;
    this.isSubmitted = false;

    const required = mode === 'topic' ? [Validators.required] : [];
    ['examId', 'lessonId', 'lessonTopicId'].forEach(control => {
      this.fg.get(control).setValidators(required);
      this.fg.get(control).updateValueAndValidity({emitEvent: false});
    });

    this.fg.get('message').setValidators(mode === 'text' ? [Validators.required, Validators.maxLength(255)] : []);
    this.fg.get('message').updateValueAndValidity({emitEvent: false});
  }

  onSubmit() {
    this.isSubmitted = true;
    if (this.fg.invalid) {
      return;
    }

    const value = this.fg.value;
    const payload = this.mode === 'topic'
      ? {examId: value.examId, lessonId: value.lessonId, lessonTopicId: value.lessonTopicId}
      : {message: value.message};

    this.isLoading = true;
    this.planService.createWeekItem(this.studentId, this.weekId, payload, this.periodId)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(() => this.modal.close(true));
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
